import type { FindResumeEngagementQuery } from './__generated__/findResumeEngagement.gqlgenerated';

export interface EngagementSender {
    name?: string | null;
    email?: string | null;
    id: string;
}

export interface EngagementRecipient {
    id: string;
    email?: string | null;
    phoneNumber?: string | null;
}

export interface EngagementJob {
    title?: string | null;
    company?: string | null;
    description?: string | null;
    employerJobId?: string | null;
    htmlDescription?: string | null;
}

export interface EngagementConversation {
    key: string;
    initialMessage: {
        id: string;
        textBody?: string | null;
        htmlBody?: string | null;
    };
}

export interface EngagementStatusHistory {
    status: string;
    timestamp: number;
    modifiedDate: string;
}

export interface EngagementFeedback {
    declineReason?: string | null;
    comment?: string | null;
}

export interface ResumeEngagement {
    id: string;
    sender: EngagementSender;
    status: string;
    createdDate: string;
    type: string;
    recipient: EngagementRecipient;
    job?: EngagementJob | null;
    conversation: EngagementConversation;
    statusHistory: EngagementStatusHistory[];
    feedback?: EngagementFeedback | null;
    fullApplyRequested: boolean;
}

export interface FindResumeEngagementData {
    engagements: ResumeEngagement[];
}

export interface FindResumeEngagementResult {
    data?: FindResumeEngagementData;
    loading: boolean;
    error?: Error;
    refetch: () => Promise<any>;
}

/**
 * Filters engagements by company name and job title from the current job.
 * This is a local fallback filtering that doesn't require EJMS conversion.
 *
 * NOTE: This filtering is only applied to GraphQL query results when the
 * rn_viewjob_onegraph_tst is active (grp 2), not to pre-filtered server data.
 *
 * @param engagements - The list of resume engagements from GraphQL query
 * @param jobCompanyName - The company name from the current job (optional)
 * @param jobTitle - The job title from the current job (optional)
 * @returns Filtered engagements or original engagements if no filter criteria provided
 */
const filterByCompanyAndTitle = (
    engagements: NonNullable<FindResumeEngagementQuery['findResumeEngagements']>['engagements'],
    jobCompanyName?: string | null,
    jobTitle?: string | null,
): NonNullable<FindResumeEngagementQuery['findResumeEngagements']>['engagements'] => {
    if (!jobCompanyName || !jobTitle) {
        return engagements;
    }

    const filteredEngagements = engagements.filter((engagement) => {
        if (!engagement?.job) {
            return false;
        }

        const engagementCompany = engagement.job.company;
        const engagementTitle = engagement.job.title;

        // Both company and title must match (case-insensitive)
        return (
            engagementCompany?.toLowerCase() === jobCompanyName.toLowerCase() &&
            engagementTitle?.toLowerCase() === jobTitle.toLowerCase()
        );
    });

    return filteredEngagements;
};

/**
 * Builds resume engagement data from GraphQL query results with optional local filtering.
 *
 * When called with jobCompanyName and jobTitle (from rn_viewjob_onegraph_tst grp 2),
 * filters engagements to match the current job's company and title.
 *
 * @param jobKey - The job key to filter engagements for
 * @param resumeEngagementData - Resume engagement data from GraphQL query
 * @param jobCompanyName - Optional company name for local filtering (OneGraph only)
 * @param jobTitle - Optional job title for local filtering (OneGraph only)
 * @returns Transformed and optionally filtered resume engagement data
 */
export const resumeEngagementDataBuilder = (
    jobKey: string,
    resumeEngagementData: FindResumeEngagementQuery | undefined,
    jobCompanyName?: string | null,
    jobTitle?: string | null,
): FindResumeEngagementData | undefined => {
    if (!resumeEngagementData?.findResumeEngagements?.engagements) {
        return undefined;
    }

    const filteredEngagements = filterByCompanyAndTitle(
        resumeEngagementData.findResumeEngagements.engagements,
        jobCompanyName,
        jobTitle,
    );

    // If no matching engagements found after filtering,
    // or more than one matching engagement found, return undefined.
    // This is to ensure we ONLY return the RIGHT engagement.
    if (filteredEngagements.length === 0 || filteredEngagements.length > 1) {
        return undefined;
    }

    return getResumeEngagementData(jobKey, filteredEngagements);
};

const getResumeEngagementData = (
    jobKey: string,
    engagements: NonNullable<FindResumeEngagementQuery['findResumeEngagements']>['engagements'],
): FindResumeEngagementData | undefined => {
    // TODO: jobKey to employerJobId interconversion pending (dependent on REZCONQL-207)
    // Returning filtered engagement data based on company name and job title

    return {
        engagements: engagements.map(
            (engagement): ResumeEngagement => ({
                id: engagement.id,
                sender: engagement.sender,
                status: engagement.status,
                createdDate: engagement.createdDate,
                type: engagement.type,
                recipient: engagement.recipient,
                job: engagement.job,
                conversation: engagement.conversation,
                statusHistory: engagement.statusHistory.map((history) => ({
                    status: history.status,
                    timestamp: history.timestamp,
                    modifiedDate: history.modifiedDate,
                })),
                feedback: engagement.feedback,
                fullApplyRequested: engagement.fullApplyRequested,
            }),
        ),
    };
};
